import {
  ApartmentOutlined,
  HomeOutlined,
  LogoutOutlined,
  SwapOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import {
  AvatarDropdown as CoreAvatarDropdown,
  type CoreUserMenuItem,
  useSession,
  useShell,
  useTenant,
} from '@zhiyue/core';
import { Avatar } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';

const AvatarDropdown: React.FC = () => {
  const intl = useIntl();
  const { currentUser } = useSession();
  const tenantState = useTenant();
  const { shellConfig } = useShell();
  const tenants = tenantState?.tenants ?? [];
  const currentTenantUuid = tenantState?.currentTenantUuid;
  const userMenuLabels = shellConfig.ui.userMenu.labels;
  const resolvedCurrentUser = currentUser as
    | (typeof currentUser & API.CurrentUser)
    | null;

  const personalCenterUrl = BizAppConfig.appPersonalCenterUrl;
  const workbenchUrl = BizAppConfig.appWorkbenchUrl?.trim();
  const tenantManagementBaseUrl = BizAppConfig.appTenantManagementUrl?.trim();
  const activeTenantUuid = currentTenantUuid || currentUser?.tenantUuid;
  const activeTenant = React.useMemo(
    () => tenants.find((tenant) => tenant.id === activeTenantUuid),
    [activeTenantUuid, tenants],
  );

  const activeTenantName = activeTenant?.name ?? userMenuLabels.unknownTenant;
  const isCurrentTenantAdmin = activeTenant?.roleLabel === '管理员';

  const tenantManagementUrl = React.useMemo(() => {
    if (!tenantManagementBaseUrl || !activeTenantUuid) {
      return '';
    }

    const organizationUuid =
      resolvedCurrentUser?.organizationUuid ||
      activeTenant?.organizations?.[0]?.orgId ||
      activeTenant?.organizations?.[0]?.id ||
      '';

    if (
      tenantManagementBaseUrl.includes('{tenantUuid}') ||
      tenantManagementBaseUrl.includes('{organizationUuid}')
    ) {
      return tenantManagementBaseUrl
        .replaceAll('{tenantUuid}', encodeURIComponent(activeTenantUuid))
        .replaceAll('{organizationUuid}', encodeURIComponent(organizationUuid));
    }

    if (typeof window === 'undefined') {
      return tenantManagementBaseUrl;
    }

    const url = new URL(tenantManagementBaseUrl, window.location.origin);
    url.searchParams.set('tenantUuid', activeTenantUuid);
    if (organizationUuid) {
      url.searchParams.set('organizationUuid', organizationUuid);
    }

    return url.toString();
  }, [
    activeTenant?.organizations,
    activeTenantUuid,
    resolvedCurrentUser?.organizationUuid,
    tenantManagementBaseUrl,
  ]);

  const currentMemberLabel = React.useMemo(() => {
    return currentUser?.name || '-';
  }, [currentUser?.name]);

  const avatarFallback = React.useMemo(() => {
    const normalizedLabel = currentMemberLabel?.trim();
    const chars = Array.from(normalizedLabel || '');

    return (chars.slice(-2).join('') || 'U').toUpperCase();
  }, [currentMemberLabel]);

  const items = React.useMemo<CoreUserMenuItem[]>(() => {
    const menuItems: CoreUserMenuItem[] = [
      {
        key: 'center',
        icon: <UserOutlined />,
        label: intl.formatMessage({ id: 'menu.account.center' }),
        href: personalCenterUrl,
        target: '_self',
        disabled: !personalCenterUrl,
      },
      {
        key: 'workbench',
        icon: <HomeOutlined />,
        label: intl.formatMessage({ id: 'menu.account.workbench' }),
        href: workbenchUrl,
        target: '_self',
        disabled: !workbenchUrl,
      },
      { type: 'divider' },
      {
        key: 'currentOrg',
        label: `${userMenuLabels.currentTenant}: ${activeTenantName}`,
        disabled: true,
      },
      {
        key: 'switchOrg',
        icon: <SwapOutlined />,
        label: userMenuLabels.switchTenant,
      },
    ];

    if (isCurrentTenantAdmin) {
      menuItems.push({
        key: 'tenantManagement',
        icon: <ApartmentOutlined />,
        label: intl.formatMessage({ id: 'menu.account.tenantManagement' }),
        href: tenantManagementUrl,
        target: '_self',
        disabled: !tenantManagementUrl,
      });
    }

    menuItems.push(
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: intl.formatMessage({ id: 'menu.account.logout' }),
      },
    );

    return menuItems;
  }, [
    intl,
    personalCenterUrl,
    workbenchUrl,
    userMenuLabels,
    activeTenantName,
    isCurrentTenantAdmin,
    tenantManagementUrl,
  ]);

  return (
    <CoreAvatarDropdown
      items={items}
      labels={{
        profile: intl.formatMessage({ id: 'menu.account.center' }),
        logout: intl.formatMessage({ id: 'menu.account.logout' }),
        currentTenant: userMenuLabels.currentTenant,
        switchTenant: userMenuLabels.switchTenant,
        unknownTenant: userMenuLabels.unknownTenant,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          cursor: 'pointer',
        }}
      >
        <Avatar size={22} src={currentUser?.avatar}>
          {avatarFallback}
        </Avatar>
        <span>{currentUser?.name}</span>
      </div>
    </CoreAvatarDropdown>
  );
};

export default AvatarDropdown;
